import { useCallback, useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, Label, Input, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, toast } from '@ludolio/shared-ui';
import { Plus, GraduationCap, Calendar, Pin, PinOff, Search } from 'lucide-react';
import { useUnitsModule } from './module-context';
import type { Semester, Unit, UnitsUser } from './types';

const MAX_VISIBLE_INSTRUCTORS = 3;

const SEMESTER_LABELS: Record<Semester, string> = {
  semester_1: 'Semester 1',
  semester_2: 'Semester 2',
  summer: 'Summer',
  winter: 'Winter',
};

const SEMESTER_ORDER: Record<Semester, number> = {
  semester_1: 1,
  semester_2: 2,
  summer: 3,
  winter: 4,
};

const SEMESTER_BADGE_CLASSES: Record<Semester, string> = {
  semester_1: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300',
  semester_2: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300',
  summer: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300',
  winter: 'border-cyan-200 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950 dark:text-cyan-300',
};

function readPinnedUnits(): Unit[] {
  try {
    const parsed = JSON.parse(localStorage.getItem('pinnedUnits') ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((unit): unit is Unit => Boolean(unit && typeof unit === 'object' && 'id' in unit))
      : [];
  } catch {
    return [];
  }
}

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function getInstructorName(instructor: UnitsUser) {
  return [instructor.firstName, instructor.lastName].filter(Boolean).join(' ') || instructor.email;
}

function InstructorSummary({ instructors }: { instructors: UnitsUser[] }) {
  const visibleInstructors = instructors.slice(0, MAX_VISIBLE_INSTRUCTORS);
  const remainingInstructors = instructors.slice(MAX_VISIBLE_INSTRUCTORS);
  const remainingCount = remainingInstructors.length;

  return (
    <div className="mt-4 space-y-1.5">
      <p className="text-sm font-semibold leading-5 text-[hsl(var(--color-foreground))]">Instructors</p>
      {instructors.length === 0 && (
        <p className="text-sm leading-5 text-[hsl(var(--color-muted-foreground))]">No instructors</p>
      )}
      {visibleInstructors.map((instructor) => (
        <p key={instructor.id} className="truncate text-sm leading-5 text-[hsl(var(--color-muted-foreground))]">
          {getInstructorName(instructor)}
        </p>
      ))}
      {remainingCount > 0 && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="-ml-1 inline-flex min-h-7 items-center rounded-sm px-1 text-left text-xs font-medium text-[hsl(var(--color-muted-foreground))] underline-offset-4 transition-colors hover:text-[hsl(var(--color-foreground))] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--color-ring))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--color-background))]"
                aria-label={`Show ${remainingCount} more instructors`}
              >
                +{remainingCount} more
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="start" className="max-w-[calc(100vw-2rem)] p-2">
              <ul className="max-h-60 space-y-1 overflow-y-auto">
                {remainingInstructors.map((instructor) => (
                  <li key={instructor.id} className="break-words text-xs leading-5">
                    {getInstructorName(instructor)}
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export function UnitsView() {
  const { api, capabilities, navigateToUnit, reportError } = useUnitsModule();
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [listMode, setListMode] = useState(capabilities.listMode);
  const includeArchived = capabilities.canShowArchived;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all');
  const [pinnedUnitIds, setPinnedUnitIds] = useState<string[]>(() =>
    capabilities.canPin ? readPinnedUnits().map((unit) => unit.id) : []
  );

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    year: new Date().getFullYear(),
    semester: 'semester_1' as Semester,
  });

  const loadUnits = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api.listUnits(listMode, includeArchived);
      if (!response.success || !response.data) {
        throw new Error(response.error || response.message || 'Failed to load units');
      }
      setUnits(response.data);
    } catch (error) {
      console.error('Failed to load units:', error);
      reportError(error);
      setLoadError(getErrorMessage(error, 'Failed to load units'));
    } finally {
      setLoading(false);
    }
  }, [api, includeArchived, listMode, reportError]);

  useEffect(() => {
    void loadUnits();
  }, [loadUnits]);

  const handleCreateUnit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      const response = await api.createUnit({
        ...formData,
        code: formData.code.trim(),
        name: formData.name.trim(),
      });
      if (!response.success) {
        throw new Error(response.error || response.message || 'Failed to create unit');
      }
      setCreateDialogOpen(false);
      setFormData({
        code: '',
        name: '',
        year: new Date().getFullYear(),
        semester: 'semester_1',
      });
      await loadUnits();
      toast.success('Unit created');
    } catch (error) {
      console.error('Failed to create unit:', error);
      reportError(error);
      toast.error(getErrorMessage(error, 'Failed to create unit'));
    } finally {
      setIsCreating(false);
    }
  };

  const togglePinUnit = (unit: Unit) => {
    const pinnedUnits = readPinnedUnits();
    
    const isPinned = pinnedUnits.some(u => u.id === unit.id);
    
    let newPinnedUnits: Unit[];
    if (isPinned) {
      newPinnedUnits = pinnedUnits.filter(u => u.id !== unit.id);
    } else {
      newPinnedUnits = [...pinnedUnits, unit];
    }
    
    localStorage.setItem('pinnedUnits', JSON.stringify(newPinnedUnits));
    setPinnedUnitIds(newPinnedUnits.map(u => u.id));
    
    // Trigger a storage event to update the sidebar
    window.dispatchEvent(new Event('storage'));
  };

  const visibleUnits = units.filter((unit) => {
    const query = search.trim().toLowerCase();
    const matchesSearch = !query
      || unit.code.toLowerCase().includes(query)
      || unit.name.toLowerCase().includes(query)
      || unit.assignedStaff?.some((staff) => {
        const name = [staff.firstName, staff.lastName].filter(Boolean).join(' ');
        return name.toLowerCase().includes(query) || staff.email.toLowerCase().includes(query);
      });
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'archived' ? unit.isArchived : !unit.isArchived);
    return matchesSearch && matchesStatus;
  });

  const groupedUnits = visibleUnits.reduce<Map<number, Unit[]>>((groups, unit) => {
    const yearUnits = groups.get(unit.year);
    if (yearUnits) {
      yearUnits.push(unit);
    } else {
      groups.set(unit.year, [unit]);
    }
    return groups;
  }, new Map());

  const sortedYearGroups = Array.from(groupedUnits, ([year, yearUnits]) => ({
    year,
    units: yearUnits.sort((a, b) => SEMESTER_ORDER[a.semester] - SEMESTER_ORDER[b.semester]),
  })).sort((a, b) => b.year - a.year);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading units...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Units</h1>
        </div>
        {capabilities.canCreate && !loadError && <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Create Unit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Unit</DialogTitle>
              <DialogDescription>
                Create a new unit (course/class) to manage students and games.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUnit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Unit Code</Label>
                <Input
                  id="code"
                  placeholder="e.g., CS101"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  minLength={2}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Unit Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Introduction to Computer Science"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  minLength={2}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Input
                  id="year"
                  type="number"
                  min="2000"
                  max="2100"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="semester">Semester</Label>
                <Select
                  value={formData.semester}
                  onValueChange={(value) => setFormData({ ...formData, semester: value as Semester })}
                >
                  <SelectTrigger id="semester">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semester_1">Semester 1</SelectItem>
                    <SelectItem value="semester_2">Semester 2</SelectItem>
                    <SelectItem value="summer">Summer</SelectItem>
                    <SelectItem value="winter">Winter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Unit'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      {capabilities.canSwitchListMode && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={listMode} onValueChange={(value) => setListMode(value as typeof listMode)}>
            <SelectTrigger className="w-44" aria-label="Choose unit list">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="assigned">Assigned units</SelectItem>
              <SelectItem value="org">Organization units</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {capabilities.canShowArchived && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search units or instructors"
              aria-label="Search units or instructors"
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}>
            <SelectTrigger className="w-full sm:w-36" aria-label="Filter units by status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-semibold">Units could not be loaded</h3>
            <p className="mt-2 text-muted-foreground">{loadError}</p>
            <Button className="mt-4" variant="outline" onClick={() => void loadUnits()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : sortedYearGroups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GraduationCap className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              {units.length === 0 ? 'No units yet' : 'No matching units'}
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              {units.length === 0
                ? 'Create your first unit to start managing students and games.'
                : 'Try another search or status filter.'}
            </p>
            {capabilities.canCreate && units.length === 0 && <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Unit
            </Button>}
          </CardContent>
        </Card>
      ) : (
        sortedYearGroups.map((group) => (
          <div key={group.year} className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Calendar className="w-5 h-5 text-muted-foreground" />
              <h2 className="text-xl font-semibold">{group.year}</h2>
              <Badge variant="outline">
                {group.units.length} {group.units.length === 1 ? 'unit' : 'units'}
              </Badge>
            </div>
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              role="list"
              aria-label={`${group.year} units`}
            >
              {group.units.map((unit) => {
                const isPinned = pinnedUnitIds.includes(unit.id);
                return (
                  <Card key={unit.id} className="flex h-full flex-col transition-shadow hover:shadow-lg" role="listitem">
                    <CardHeader className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <CardTitle className="min-w-0 break-words text-xl font-bold leading-tight">{unit.code}</CardTitle>
                            <Badge className={SEMESTER_BADGE_CLASSES[unit.semester]}>
                              {SEMESTER_LABELS[unit.semester]}
                            </Badge>
                            {capabilities.canShowArchived && unit.isArchived && <Badge variant="secondary">Archived</Badge>}
                          </div>
                          <CardDescription className="mt-2 min-h-12 break-words text-base font-medium leading-6 text-[hsl(var(--color-foreground))]">
                            {unit.name}
                          </CardDescription>
                          {capabilities.showAssignedStaff && <InstructorSummary instructors={unit.assignedStaff ?? []} />}
                        </div>
                        {capabilities.canPin && <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            togglePinUnit(unit);
                          }}
                          title={isPinned ? 'Unpin unit' : 'Pin unit'}
                          aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${unit.code}`}
                        >
                          {isPinned ? (
                            <PinOff className="w-4 h-4 text-primary" />
                          ) : (
                            <Pin className="w-4 h-4" />
                          )}
                        </Button>}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" className="w-full" onClick={() => navigateToUnit(unit.id)}>
                          View Details
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

